(function() {
  'use strict';

  window.theme = window.theme || {};

  if (theme.productGallery) {
    return;
  }

  var instances = new WeakMap();

  function requestIdle(callback) {
    if (typeof window.requestIdleCallback === 'function') {
      return window.requestIdleCallback(callback, { timeout: 2000 });
    }

    var start = Date.now();
    return window.setTimeout(function() {
      callback({
        didTimeout: false,
        timeRemaining: function() {
          return Math.max(0, 50 - (Date.now() - start));
        }
      });
    }, 1);
  }

  function cancelIdle(handle) {
    if (typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(handle);
    } else {
      window.clearTimeout(handle);
    }
  }

  function ProductGallery(root, options) {
    this.root = root;
    this.product = options.product || null;
    this.modalId = options.modalId || null;
    this.destroyed = false;
    this.idleHandle = null;
    this.tasks = [];
    this.listeners = [];
    this.timers = new Set();
    this.observer = null;
    this.boundNavigationButtons = new WeakSet();
    this.loadedLocalVideos = new WeakSet();
    this.previousNavigationPending = false;
    this.variantUpdateToken = 0;
    this.controller = typeof window.AbortController === 'function' ? new AbortController() : null;
    this.sliders = {
      features: null,
      scenes: null,
      video: null
    };

    this.wrapper = root.querySelector('[data-product-images]');
    this.productPhotos = root.querySelector('[data-product-images] > div.product__photos');
    this.oldFeaturesPhotos = root.querySelector('[data-product-old-features-photos]');
    this.featuresPhotos = root.querySelector('[data-product-features-photos]');
    this.featuresThumbs = root.querySelector('[data-product-features-thumbs]');
    this.scenesPhotos = root.querySelector('[data-product-scenes-photos]');
    this.scenesThumbs = root.querySelector('[data-product-scenes-thumbs]');
    this.videoPhotos = root.querySelector('[data-product-video-photos]');
    this.videoThumbs = root.querySelector('[data-product-video-thumbs]');
    this.switchers = Array.prototype.slice.call(root.querySelectorAll('.media_button_list .switcher'));

    var activeSwitcher = root.querySelector('.media_button_list .switcher.active');
    this.activeType = activeSwitcher ? activeSwitcher.dataset.type : 'image';
    this.valid = !!(this.wrapper && this.productPhotos);

    if (this.valid) {
      this.queueInitialization();
    }
  }

  ProductGallery.prototype.update = function(options) {
    if (options.product) {
      this.product = options.product;
    }

    if (options.modalId) {
      this.modalId = options.modalId;
    }
  };

  ProductGallery.prototype.getProduct = function() {
    if (this.product) {
      return this.product;
    }

    if (!theme.sections || !theme.sections.instances) {
      return null;
    }

    this.product = theme.sections.instances.find(function(instance) {
      return instance.type === 'product' && instance.container === this.root;
    }.bind(this)) || null;

    return this.product;
  };

  ProductGallery.prototype.listen = function(target, eventName, handler) {
    if (!target) {
      return;
    }

    if (this.controller) {
      try {
        target.addEventListener(eventName, handler, { signal: this.controller.signal });
        this.listeners.push({
          target: target,
          eventName: eventName,
          handler: handler
        });
        return;
      } catch (error) {
        // Older Safari versions do not support AbortSignal in event options.
      }
    }

    target.addEventListener(eventName, handler);
    this.listeners.push({
      target: target,
      eventName: eventName,
      handler: handler
    });
  };

  ProductGallery.prototype.setTimer = function(callback, delay) {
    var timer = window.setTimeout(function() {
      this.timers.delete(timer);
      callback();
    }.bind(this), delay);

    this.timers.add(timer);
    return timer;
  };

  ProductGallery.prototype.queueInitialization = function() {
    if (!this.oldFeaturesPhotos && !this.scenesPhotos && !this.videoPhotos) {
      this.productPhotos.classList.remove('init_height');
    }

    if (this.scenesPhotos) {
      this.tasks.push(this.initScenesSlider.bind(this));
    }

    if (this.featuresPhotos) {
      if (!this.featuresPhotos.hasAttribute('data-product-photos')) {
        this.tasks.push(this.initFeaturesSlider.bind(this));
      } else if (!this.modalId) {
        this.tasks.push(this.ensurePrimaryFeaturesSlider.bind(this));
      }
    }

    if (this.videoPhotos) {
      this.tasks.push(this.initVideoSlider.bind(this));
    }

    if (this.modalId) {
      this.bindNavigation();
    }
    this.bindSwitchers();
    this.bindVariantUpdates();

    if (!this.modalId) {
      this.tasks.push(this.bindNavigation.bind(this));
    }

    if (!this.modalId) {
      this.tasks.push(this.syncPreselectedVariant.bind(this));
    }

    if (this.tasks.length) {
      this.idleHandle = requestIdle(this.processTasks.bind(this));
    }
  };

  ProductGallery.prototype.runTask = function(task) {
    try {
      task();
    } catch (error) {
      if (window.console && typeof console.error === 'function') {
        console.error('Product gallery initialization failed', error);
      }
    }
  };

  ProductGallery.prototype.processTasks = function(deadline) {
    this.idleHandle = null;

    if (this.destroyed) {
      return;
    }

    var processed = false;
    while (this.tasks.length && (!processed || deadline.timeRemaining() > 1)) {
      var task = this.tasks.shift();
      this.runTask(task);

      processed = true;

      if (!deadline.didTimeout && deadline.timeRemaining() <= 1) {
        break;
      }
    }

    if (this.tasks.length) {
      this.idleHandle = requestIdle(this.processTasks.bind(this));
    }
  };

  ProductGallery.prototype.prepareSlide = function(slide) {
    // Video groups have their own activation path; idle slider setup is not demand.
    if (slide && slide.closest('[data-product-video-photos]')) {
      return;
    }
    var product = this.getProduct();

    if (product && product.flickity && product.settings.imageSetName && typeof product.prepMediaOnSlide === 'function') {
      product.prepMediaOnSlide(slide);
    }
  };

  ProductGallery.prototype.createSlider = function(type, photos, thumbs, onInit, onChange) {
    if (this.sliders[type]) {
      return this.sliders[type];
    }

    if (!photos || !thumbs || typeof theme.Slideshow !== 'function') {
      return null;
    }

    var options = {
      dragThreshold: 25,
      adaptiveHeight: true,
      avoidReflow: true,
      initialIndex: 0,
      childNav: thumbs,
      childNavScroller: thumbs.querySelector('.product__thumbs--scroller'),
      childVertical: thumbs.dataset.position === 'beside',
      wrapAround: false,
      prevNextButtons: true,
      pageDots: photos.hasAttribute('data-dots'),
      callbacks: {
        onInit: function(slide) {
          this.prepareSlide(slide);
          onInit();
        }.bind(this)
      }
    };

    if (typeof onChange === 'function') {
      options.callbacks.onChange = onChange;
    }

    this.sliders[type] = new theme.Slideshow(photos, options);
    return this.sliders[type];
  };

  ProductGallery.prototype.syncGroupVisibility = function(type) {
    var photos = this.root.querySelector('[data-product-' + type + '-photos]');
    var thumbs = this.root.querySelector('[data-product-' + type + '-thumbs]');
    var isActive = this.activeType === type;

    [photos, thumbs].forEach(function(element) {
      if (!element) {
        return;
      }

      if (isActive) {
        element.classList.remove('image-invisible', 'hide');
      } else {
        element.classList.add('image-invisible');
      }
    });
  };

  ProductGallery.prototype.initScenesSlider = function() {
    this.createSlider('scenes', this.scenesPhotos, this.scenesThumbs, function() {
      this.syncGroupVisibility('scenes');

      if (!this.videoPhotos) {
        this.productPhotos.classList.remove('init_height');
      }
    }.bind(this));
  };

  ProductGallery.prototype.initFeaturesSlider = function() {
    this.createSlider('features', this.featuresPhotos, this.featuresThumbs, function() {
      this.syncGroupVisibility('features');

      if (!this.videoPhotos && !this.scenesPhotos) {
        this.productPhotos.classList.remove('init_height');
      }
    }.bind(this));
  };

  ProductGallery.prototype.ensurePrimaryFeaturesSlider = function() {
    if (!this.featuresPhotos || !this.featuresPhotos.hasAttribute('data-product-photos')) {
      return;
    }

    var product = this.getProduct();
    if (product && product.flickity) {
      this.syncGroupVisibility('features');
      return;
    }

    var slideCount = this.featuresPhotos.querySelectorAll('.product-main-slide').length;
    if (slideCount <= 1 && !this.sliders.features) {
      this.initFeaturesSlider();
    }
  };

  ProductGallery.prototype.ensureSlider = function(type) {
    if (type === 'features') {
      if (this.featuresPhotos && this.featuresPhotos.hasAttribute('data-product-photos')) {
        this.ensurePrimaryFeaturesSlider();
      } else {
        this.initFeaturesSlider();
      }
    } else if (type === 'scenes') {
      this.initScenesSlider();
    } else if (type === 'video') {
      this.initVideoSlider();
    }
  };

  ProductGallery.prototype.initVideoSlider = function() {
    this.createSlider('video', this.videoPhotos, this.videoThumbs, function() {
      this.syncGroupVisibility('video');
      this.productPhotos.classList.remove('init_height');
      if (this.activeType === 'video' && !this.modalId) {
        this.loadSelectedVideo();
      }
    }.bind(this), function(index) {
      this.pauseVideos();
      if (this.activeType === 'video') {
        this.loadVideoAtIndex(index);
      }
    }.bind(this));
  };

  ProductGallery.prototype.pauseVideos = function() {
    var product = this.getProduct();
    if (product && typeof product.stopVideos === 'function') {
      product.stopVideos();
    }
    var scope = this.wrapper || this.root;

    scope.querySelectorAll('video').forEach(function(video) {
      if (typeof video.pause === 'function' && !video.paused) {
        video.pause();
      }
    });
  };

  ProductGallery.prototype.loadLocalVideo = function(video) {
    if (
      !video ||
      video.dataset.videoType !== 'mp4' ||
      this.loadedLocalVideos.has(video)
    ) {
      return;
    }

    if (video.readyState > 0 || typeof video.load !== 'function') {
      this.loadedLocalVideos.add(video);
      return;
    }

    // Videos parsed in an inert document need resource selection restarted once adopted.
    video.preload = 'metadata';
    try {
      if (
        video.networkState === video.NETWORK_EMPTY ||
        video.networkState === video.NETWORK_NO_SOURCE
      ) {
        video.load();
      }
      this.loadedLocalVideos.add(video);
    } catch (error) {
      video.removeAttribute('preload');
    }
  };

  ProductGallery.prototype.loadVideoInSlide = function(slide) {
    if (!slide || this.destroyed || this.activeType !== 'video') {
      return;
    }

    var wrapper = slide.querySelector('.product__video-wrapper[data-video-poster]');
    if (wrapper && !wrapper.classList.contains('product__video-wrapper--has-poster')) {
      var poster = wrapper.getAttribute('data-video-poster');
      if (poster) {
        var escapedPoster = poster.replace(/["\\]/g, function(character) {
          return '\\' + character;
        });
        wrapper.style.backgroundImage = 'url("' + escapedPoster + '")';
        wrapper.classList.add('product__video-wrapper--has-poster');
      }
    }

    var product = this.getProduct();
    if (product && typeof product.videoSetup === 'function') {
      product.videoSetup(slide);
    }
    this.loadLocalVideo(slide.querySelector('video.product__video[data-video-type="mp4"]'));
  };

  ProductGallery.prototype.loadVideoAtIndex = function(index) {
    if (!this.videoPhotos) {
      return;
    }

    var slides = this.videoPhotos.querySelectorAll('.product-main-slide');
    this.loadVideoInSlide(slides[index]);
  };

  ProductGallery.prototype.loadSelectedVideo = function() {
    if (!this.videoPhotos) {
      return;
    }

    var slide = this.videoPhotos.querySelector('.product-main-slide.is-selected') ||
      this.videoPhotos.querySelector('.product-main-slide');
    this.loadVideoInSlide(slide);
  };

  ProductGallery.prototype.getSlider = function(type) {
    if (type === 'image') {
      var product = this.getProduct();
      return product ? product.flickity : null;
    }

    if (type === 'features' && this.featuresPhotos && this.featuresPhotos.hasAttribute('data-product-photos')) {
      var primaryProduct = this.getProduct();
      return (primaryProduct && primaryProduct.flickity) || this.sliders.features;
    }

    return this.sliders[type] || null;
  };

  ProductGallery.prototype.resizeSlider = function(type) {
    var slider = this.getSlider(type);

    if (slider && typeof slider.resize === 'function') {
      slider.resize();
    }
  };

  ProductGallery.prototype.activateSwitcher = function(switcher, force) {
    var type = switcher && switcher.dataset.type;

    if (!type || (!force && this.activeType === type)) {
      return;
    }

    var nextPhotos = this.root.querySelector('[data-product-' + type + '-photos]');
    var nextThumbs = this.root.querySelector('[data-product-' + type + '-thumbs]');
    if (!nextPhotos || !nextThumbs) {
      return;
    }

    if (this.activeType === 'video' && type !== 'video') {
      this.pauseVideos();
    }

    this.activeType = type;
    this.switchers.forEach(function(item) {
      item.classList.toggle('active', item === switcher);
      this.syncGroupVisibility(item.dataset.type);
    }.bind(this));

    this.resizeSlider(type);

    if (type === 'video') {
      this.loadSelectedVideo();
    }
  };

  ProductGallery.prototype.bindSwitchers = function() {
    this.switchers.forEach(function(switcher) {
      this.listen(switcher, 'click', function() {
        this.ensureSlider(switcher.dataset.type);
        this.productPhotos.classList.remove('init_height');
        this.activateSwitcher(switcher, false);
        this.bindAdjacentNavigation();
      }.bind(this));
    }.bind(this));
  };

  ProductGallery.prototype.bindNavigation = function() {
    if (this.modalId) {
      this.listen(document, 'modalClose.' + this.modalId, function() {
        this.pauseVideos();
      }.bind(this));

      this.listen(document, 'modalOpen.' + this.modalId, function() {
        var firstSwitcher = this.switchers[0];
        this.ensureSlider(firstSwitcher && firstSwitcher.dataset.type);
        this.productPhotos.classList.remove('init_height');
        this.activateSwitcher(firstSwitcher, true);
        var openingType = this.activeType;

        this.setTimer(function() {
          if (this.activeType === openingType) {
            this.activateSwitcher(firstSwitcher, true);
          } else {
            this.resizeSlider(this.activeType);
          }
          this.bindAdjacentNavigation();
        }.bind(this), 50);
      }.bind(this));
    } else {
      this.bindAdjacentNavigation();
    }
  };

  ProductGallery.prototype.bindAdjacentNavigation = function() {
    if (this.destroyed) {
      return;
    }

    var groups = this.root.querySelectorAll('[data-product-images][data-product-id] .product__main-photos');

    if (!groups.length) {
      return;
    }

    groups.forEach(function(group, index) {
      var slides = group.querySelectorAll('.product-slideshow:not([data-product-video-photos], [data-product-model-photos]) .product-main-slide');
      var previousButton = group.querySelector('.flickity-previous');
      var nextButton = group.querySelector('.flickity-next');

      if (!slides.length) {
        return;
      }

      var firstIndex = Number(slides[0].dataset.index);
      var lastIndex = Number(slides[slides.length - 1].dataset.index);

      if (previousButton && !this.boundNavigationButtons.has(previousButton)) {
        this.boundNavigationButtons.add(previousButton);
        previousButton.removeAttribute('disabled');
        this.listen(previousButton, 'click', function() {
          var selectedSlide = group.querySelector('.is-selected');
          if (!selectedSlide) {
            return;
          }

          var selectedIndex = Number(selectedSlide.dataset.index);
          if (previousButton.disabled) {
            previousButton.removeAttribute('disabled');
            if (!this.previousNavigationPending) {
              this.previousNavigationPending = true;
              return;
            }
          }

          if (selectedIndex === firstIndex && this.switchers[index - 1]) {
            this.switchers[index - 1].click();
          }
        }.bind(this));
      }

      if (nextButton && !this.boundNavigationButtons.has(nextButton)) {
        this.boundNavigationButtons.add(nextButton);
        nextButton.removeAttribute('disabled');
        this.listen(nextButton, 'click', function() {
          var selectedSlide = group.querySelector('.is-selected');
          if (!selectedSlide) {
            return;
          }

          if (nextButton.disabled) {
            nextButton.removeAttribute('disabled');
            return;
          }

          var selectedIndex = Number(selectedSlide.dataset.index);
          if (selectedIndex === lastIndex && this.switchers[index + 1]) {
            this.switchers[index + 1].click();
          } else {
            this.previousNavigationPending = false;
          }
        }.bind(this));
      }
    }.bind(this));

  };

  ProductGallery.prototype.refresh = function() {
    if (this.destroyed) {
      return;
    }

    this.bindAdjacentNavigation();
    this.resizeSlider(this.activeType);
  };

  ProductGallery.prototype.findSwitcher = function(type) {
    return this.switchers.find(function(switcher) {
      return switcher.dataset.type === type;
    }) || null;
  };

  ProductGallery.prototype.enableNavigation = function(photos) {
    if (!photos) {
      return;
    }

    var previousButton = photos.querySelector('.flickity-previous');
    var nextButton = photos.querySelector('.flickity-next');

    if (previousButton) {
      previousButton.removeAttribute('disabled');
    }
    if (nextButton) {
      nextButton.removeAttribute('disabled');
    }
  };

  ProductGallery.prototype.bindVariantUpdates = function() {
    this.listen(this.root, 'variantSlide:update', function(event) {
      var imageIndex = event.detail && event.detail.imageIndex;
      if (typeof imageIndex === 'undefined') {
        return;
      }

      var targetType = this.oldFeaturesPhotos ? 'features' : this.activeType;
      var switcher = this.oldFeaturesPhotos ? this.findSwitcher('features') : this.switchers[0];

      if (switcher) {
        switcher.click();
        targetType = switcher.dataset.type;
      }

      var updateToken = ++this.variantUpdateToken;
      var goToVariant = function(attempt) {
        if (updateToken !== this.variantUpdateToken || this.destroyed) {
          return;
        }

        var slider = this.getSlider(targetType);
        if (!slider || typeof slider.goToSlide !== 'function') {
          if (attempt < 20) {
            this.setTimer(function() {
              goToVariant(attempt + 1);
            }, 50);
          }
          return;
        }

        slider.goToSlide(imageIndex);
        this.previousNavigationPending = true;
        this.enableNavigation(this.root.querySelector('[data-product-' + targetType + '-photos]'));
      }.bind(this);

      this.setTimer(function() {
        goToVariant(0);
      }, 20);
    }.bind(this));
  };

  ProductGallery.prototype.syncPreselectedVariant = function() {
    var inputs = this.root.querySelectorAll('.variant-wrapper .variant-input [data-variant-input]');
    var selectedIndex = -1;

    inputs.forEach(function(input, index) {
      if (input.checked) {
        selectedIndex = index;
      }
    });

    if (selectedIndex <= 0) {
      return;
    }

    var applySelection = function() {
      var buttons = this.root.querySelectorAll('.swatch-view-item .swatch-button');
      if (!buttons.length || !buttons[selectedIndex]) {
        return false;
      }

      buttons[0].click();
      this.setTimer(function() {
        if (buttons[selectedIndex] && buttons[selectedIndex].isConnected) {
          buttons[selectedIndex].click();
          this.previousNavigationPending = true;
        }
      }.bind(this), 50);
      return true;
    }.bind(this);

    if (applySelection() || typeof window.MutationObserver !== 'function') {
      return;
    }

    this.observer = new MutationObserver(function() {
      if (applySelection()) {
        this.observer.disconnect();
        this.observer = null;
      }
    }.bind(this));
    this.observer.observe(this.root, { childList: true, subtree: true });

    this.setTimer(function() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }.bind(this), 15000);
  };

  ProductGallery.prototype.destroy = function() {
    if (this.destroyed) {
      return;
    }

    this.pauseVideos();
    this.destroyed = true;

    if (this.idleHandle !== null) {
      cancelIdle(this.idleHandle);
      this.idleHandle = null;
    }

    if (this.controller) {
      this.controller.abort();
    }

    this.listeners.forEach(function(listener) {
      listener.target.removeEventListener(listener.eventName, listener.handler);
    });
    this.listeners = [];

    this.timers.forEach(function(timer) {
      window.clearTimeout(timer);
    });
    this.timers.clear();

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    Object.keys(this.sliders).forEach(function(type) {
      var slider = this.sliders[type];
      if (slider && typeof slider.destroy === 'function') {
        slider.destroy();
      }
      this.sliders[type] = null;
    }.bind(this));
  };

  theme.productGallery = {
    init: function(root, options) {
      if (!root) {
        return null;
      }

      options = options || {};
      var instance = instances.get(root);

      if (instance) {
        instance.update(options);
        return instance;
      }

      instance = new ProductGallery(root, options);
      if (!instance.valid) {
        return null;
      }

      instances.set(root, instance);
      return instance;
    },

    get: function(root) {
      return root ? instances.get(root) || null : null;
    },

    refresh: function(root) {
      var instance = root ? instances.get(root) : null;
      if (instance) {
        instance.refresh();
      }
    },

    destroy: function(root) {
      var instance = root ? instances.get(root) : null;
      if (!instance) {
        return;
      }

      instance.destroy();
      instances.delete(root);
    }
  };

  function getProductInstance(root) {
    if (!theme.sections || !theme.sections.instances) {
      return null;
    }

    return theme.sections.instances.find(function(instance) {
      return instance.type === 'product' && instance.container === root;
    }) || null;
  }

  function getProductRoots(scope) {
    var roots = [];

    if (!scope) {
      return roots;
    }

    if (
      scope.nodeType === 1 &&
      typeof scope.matches === 'function' &&
      scope.matches('[data-section-type="product"]')
    ) {
      roots.push(scope);
    }

    if (typeof scope.querySelectorAll === 'function') {
      scope.querySelectorAll('[data-section-type="product"]').forEach(function(root) {
        if (roots.indexOf(root) === -1) {
          roots.push(root);
        }
      });
    }

    return roots;
  }

  function initializeRoot(root) {
    var product = getProductInstance(root);
    var productId = root.getAttribute('data-product-id');
    var modalId = root.dataset.modal === 'true' && productId
      ? 'QuickShopModal-' + productId
      : null;
    var instance = theme.productGallery.init(root, {
      product: product,
      modalId: modalId
    });

    if (product && instance) {
      product.productGallery = instance;
    }

    return instance;
  }

  function initializeWithin(scope) {
    getProductRoots(scope).forEach(initializeRoot);
  }

  function destroyWithin(scope) {
    getProductRoots(scope).forEach(function(root) {
      theme.productGallery.destroy(root);
    });
  }

  function initializePageGalleries() {
    initializeWithin(document);
  }

  document.addEventListener('DOMContentLoaded', initializePageGalleries);
  document.addEventListener('page:loaded', initializePageGalleries);
  document.addEventListener('shopify:section:load', function(event) {
    initializeWithin(event.target);
  });
  document.addEventListener('shopify:section:unload', function(event) {
    destroyWithin(event.target);
  });
  document.addEventListener('quickview:loaded', function() {
    initializeWithin(document);
  });

  if (document.readyState === 'complete') {
    window.setTimeout(initializePageGalleries, 0);
  }
})();
